import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import toast from 'react-hot-toast';
import Skeleton from '../../../../components/Skeleton';
import TableTemplate from '../../../../components/table/index';
import CustomModal from '../../../../components/modal/index';
import { getAllUserRole, deleteUserRole } from '../../../../controllers/UserRoleController';

const TableComponent = ({ t }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  
  const columns = useMemo(() => [
    { field: 'name', title: t('common.role.title'), sortable: true },
  ], [t]);
  
  const actions = useMemo(() => [
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-secondary hover:text-primary focus:text-primary'
        onClick={() => navigate(`/user-role/${row.id}/edit`)}
      >
        <MdEdit />
      </button>
    ),
    (row) => (
      <button
        className='focus:outline-none text-xl md:text-2xl text-errorMessage hover:text-errorText focus:text-errorText'
        onClick={() => handleOpenModal(row.id)}
      >
        <IoMdTrash />
      </button>
    ),
  ], [navigate]);
  
  const filters = useMemo(() => [
    { field: 'name', label: t('common.role.title'), by: t('common.role.admin'), labelBy: t('common.role.admin'), mode: 'contains' },
    { field: 'name', label: t('common.role.title'), by: t('common.role.staff'), labelBy: t('common.role.staff'), mode: 'contains' },
  ], [t]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const result = await getAllUserRole();
        const formattedRows = result.roles.map((role) => {
          const formattedRoleName = role.name.toLowerCase().replace(/\s(.)/g, (match) => match.toUpperCase()).replace(/\s/g, '');
          const translatedRoleName = t(`common.role.${formattedRoleName}`, { defaultValue: role.name });
          
          return {
            id: role.id,
            name: translatedRoleName,
          };
        });
        
        setRows(formattedRows);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      };
    };
    
    fetchData();
  }, [t]);
  
  const handleAdd = () => {
    navigate('/user-role/create');
  };
  
  const handleOpenModal = (id) => {
    setSelectedId(id);
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  const handleDelete = async () => {
    try {
      await deleteUserRole(selectedId);
      setRows(rows.filter((row) => row.id !== selectedId));
      setOpen(false);
      toast.success(t('userRole.delete.message'));
    } catch (error) {
      setOpen(false);
      toast.error(error.message);
    };
  };
  
  if (loading) {
    return (
      <Skeleton className='h-96 w-full rounded-lg' />
    );
  };
  
  return (
    <div>
      <TableTemplate
        rows={rows}
        columns={columns}
        actions={actions}
        filters={filters}
        onAddNew={handleAdd}
        fileName={t('menu.userRole')}
      />
      
      <CustomModal
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title={t('userRole.delete.title')}
        description={t('userRole.delete.description')}
        confirmText={t('common.action.delete')}
        cancelText={t('common.action.cancel')}
      />
    </div>
  );
};

export default TableComponent;
